import { generate } from '@ant-design/colors';
import { mostReadable, readability, TinyColor } from '@ctrl/tinycolor';

/**
 * The surface a brand colour most often has to read against in dark mode (the card background).
 * Exported so the admin Portals preview scores against the same value this module derives against
 * instead of keeping its own copy. Keep in sync with `--card-color` in the `.dark` block of
 * libs/core/src/lib/themes/global-styles.scss.
 */
export const DARK_SURFACE = '#161b22';
// WCAG AA for body text. --primary-color is a foreground here (links, active borders, icons), not
// only a button fill, so the large-text 3:1 allowance doesn't cover it.
const MIN_CONTRAST = 4.5;
// The darkest thing that can sit on a primary-colored fill, matching the `.dark` page canvas.
const DARK_ON_PRIMARY = '#10141A';

export function generateColorPalette(
  primaryColor: string,
  secondaryColor: string,
  count: number = 5,
): string[] {
  const color1 = new TinyColor(primaryColor);
  const color2 = new TinyColor(secondaryColor);
  const palette: string[] = [];

  // Add the primary color
  palette.push(color1.toHexString());

  // Calculate intermediate colors
  const steps = count - 2; // Subtract 2 for the start and end colors
  if (steps > 0) {
    const hsl1 = color1.toHsl();
    const hsl2 = color2.toHsl();

    // Calculate the step sizes for each component
    const hStep = (hsl2.h - hsl1.h) / (steps + 1);
    const sStep = (hsl2.s - hsl1.s) / (steps + 1);
    const lStep = (hsl2.l - hsl1.l) / (steps + 1);

    // Generate intermediate colors
    for (let i = 1; i <= steps; i++) {
      const color = new TinyColor({
        h: hsl1.h + hStep * i,
        s: hsl1.s + sStep * i,
        l: hsl1.l + lStep * i,
      });
      palette.push(color.toHexString());
    }
  }

  // Add the secondary color
  palette.push(color2.toHexString());

  return palette;
}

/**
 * A chart ramp needs two anchors, but most tenants only configure a primary color - the branding
 * API returns `secondaryColor: null` for them. Rotating the hue keeps the second anchor inside the
 * tenant's own identity; the alternative (keeping whatever secondary the build shipped with) mixes
 * two different brands into one chart, which is how Swan's dashboards ended up drawing Inshuwa
 * magenta. The lighten keeps adjacent series distinguishable once the ramp is interpolated.
 */
export function deriveSecondaryColor(primaryColor: string): string {
  return new TinyColor(primaryColor).spin(60).lighten(10).toHexString();
}

/**
 * Brand colours are picked to sit on white, so most are far too dark to read on a dark surface -
 * Swan's #012f87 manages about 1.4:1 against the dark card, well under the AA floor, and it paints
 * links and active borders, not just button fills. Lightening in small steps keeps the tenant's own
 * hue (so it still reads as their brand) while lifting it over the floor, which is the same trade
 * Material 3 makes swapping its tone-40 primary for tone-80 in a dark scheme.
 */
export function deriveDarkPrimary(primaryColor: string): string {
  let color = new TinyColor(primaryColor);
  // Lightness alone terminates the loop (contrast against a near-black surface rises with it, and
  // the ceiling guards a pathological input). The desaturation is what keeps the result usable:
  // raising a dark, saturated brand purely by lightness lands it somewhere neon - #520B4D goes to a
  // highlighter magenta - and high-chroma accents vibrate against a dark background anyway. Bleeding
  // a little saturation per step lands #520B4D on a muted orchid and #012f87 on a slate blue, both
  // still recognisably the brand.
  while (readability(color, DARK_SURFACE) < MIN_CONTRAST && color.toHsl().l < 0.92) {
    color = color.lighten(4).desaturate(4);
  }
  return color.toHexString();
}

// ng-zorro's own @component-background in dark mode. antd derives its dark palette by mixing
// toward the surface, so this has to match what the Less compiled or the runtime tints drift from
// the fallbacks in theme.dark.less. Deliberately the flat #141414 rather than our brand-tinted
// --card-color: the tint is 7%, far below what shifts a palette step.
const ANTD_DARK_BACKGROUND = '#141414';

/**
 * The hover and active tints antd derives from the primary (palette steps 5 and 7), which the Less
 * bakes in at build time - see the @primary-5/@primary-7 pins in theme.less and theme.dark.less.
 * Uses antd's own generator rather than an approximation so these land on exactly the values the
 * stylesheet would have compiled, just from the tenant's colour instead of the build's.
 */
export function deriveInteractionTints(
  primaryColor: string,
  dark: boolean,
): { hover: string; active: string } {
  const palette = dark
    ? generate(primaryColor, { theme: 'dark', backgroundColor: ANTD_DARK_BACKGROUND })
    : generate(primaryColor);

  // generate() is 0-indexed over antd's 1-10 palette, so step 5 is [4] and step 7 is [6].
  return { hover: palette[4], active: palette[6] };
}

/**
 * Lightening the primary can leave the tenant's configured label colour (usually white) failing
 * against it, so a primary button would go from readable to not. Their choice is kept whenever it
 * still passes and only swapped for the better of white/near-black when it doesn't.
 */
export function resolveOnPrimary(primaryColor: string, configuredTextColor: string): string {
  if (readability(primaryColor, configuredTextColor) >= MIN_CONTRAST) return configuredTextColor;
  return mostReadable(primaryColor, ['#FFFFFF', DARK_ON_PRIMARY])?.toHexString() ?? configuredTextColor;
}
